import FootTrafficFilterApi from '@/Enums/Attribution/FootTraffic/FilterApi.enum';
import type {
  IFootTrafficAudioCampaign,
  IFootTrafficCampaignFilterOptionsResponse,
  IFootTrafficLineItemFilterOptionsResponse,
  IFootTrafficLocationFilterOptionsResponse,
  IFootTrafficOmnichannelCampaign,
  IFootTrafficOmnichannelLineItem,
  IFootTrafficYoutubeCampaign,
  IFootTrafficYoutubeLineItem,
} from '@/Interfaces/Attribution/FootTraffic.interface';
import type {
  IFootTrafficCampaignControlOption,
  IFootTrafficLineItemControlOption,
} from '@/Interfaces/Attribution/State.interface';
import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import { capitalize } from '@mui/material';

type FootTrafficCampaign = IFootTrafficOmnichannelCampaign | IFootTrafficYoutubeCampaign | IFootTrafficAudioCampaign;
type FootTrafficLineItem = IFootTrafficOmnichannelLineItem | IFootTrafficYoutubeLineItem;
type FootTrafficLineItemFilterApi = FootTrafficFilterApi.Omnichannel | FootTrafficFilterApi.Youtube;

export default class FootTrafficFilterService {
  protected static readonly campaignOptionMappers: Record<FootTrafficFilterApi, (record: any) => IControlOption> = {
    [FootTrafficFilterApi.Omnichannel]: (record: IFootTrafficOmnichannelCampaign) => ({
      label: record.name,
      value: record.id,
    }),
    [FootTrafficFilterApi.Youtube]: (record: IFootTrafficYoutubeCampaign) => ({
      label: record.displayName,
      value: record.insertionOrderId,
    }),
    [FootTrafficFilterApi.Audio]: (record: IFootTrafficAudioCampaign) => ({ label: record.name, value: record.id }),
  };

  protected static readonly lineItemOptionMappers: Record<
    FootTrafficLineItemFilterApi,
    (record: any) => IControlOption
  > = {
    [FootTrafficFilterApi.Omnichannel]: (record: IFootTrafficOmnichannelLineItem) => ({
      label: record.name,
      value: record.id,
    }),
    [FootTrafficFilterApi.Youtube]: (record: IFootTrafficYoutubeLineItem) => ({
      label: record.displayName,
      value: record.lineItemId,
    }),
  };

  public static getDefaultFootTrafficLocationOptions(): IControlOption[] {
    return [];
  }

  public static getDefaultFootTrafficLineItemOptions(): IFootTrafficLineItemControlOption[] {
    return [];
  }

  public static getDefaultFootTrafficCampaignOptions = (): IFootTrafficCampaignControlOption[] => [];

  public static processLocationFilterOptions = (
    filterOptions: IFootTrafficLocationFilterOptionsResponse,
  ): IControlOption[] => {
    return (filterOptions ?? []).map(record => ({
      label: record.address ? `${record.name}: ${record.address}` : record.name,
      value: record.id,
    }));
  };

  public static processLineItemFilterOptions = (
    filterOptions: IFootTrafficLineItemFilterOptionsResponse,
  ): IFootTrafficLineItemControlOption[] => {
    return (Object.entries(filterOptions ?? {}) as [FootTrafficLineItemFilterApi, FootTrafficLineItem[]][]).flatMap(
      ([key, options]) => {
        return (options ?? []).map(record => ({
          groupValue: key,
          groupLabel: capitalize(key),
          ...this.lineItemOptionMappers[key]?.(record),
        })) as IFootTrafficLineItemControlOption[];
      },
    );
  };

  public static processCampaignFilterOptions = (
    filterOptions: IFootTrafficCampaignFilterOptionsResponse,
  ): IFootTrafficCampaignControlOption[] => {
    return (Object.entries(filterOptions ?? {}) as [FootTrafficFilterApi, FootTrafficCampaign[]][]).flatMap(
      ([key, options]) => {
        return (options ?? []).map(record => ({
          groupValue: key,
          groupLabel: capitalize(key),
          ...this.campaignOptionMappers[key]?.(record),
        })) as IFootTrafficCampaignControlOption[];
      },
    );
  };
}
